import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { motion } from 'framer-motion'
import { BOOKING_URL } from '../../data/site'
import { srcSetFor } from '../../utils/images'

// useLayoutEffect warns during SSR (it can't run server-side). Fall back to
// useEffect on the server so prerendering stays warning-free; the client still
// gets layout-synced positioning.
const useIsomorphicLayoutEffect =
  typeof window !== 'undefined' ? useLayoutEffect : useEffect

// Vertical fraction of each source image marking the bottom edge of the
// physical "Recreo Rehab" sign. The overlay copy is anchored just beneath it
// so the headline always reads as a sub-line under the sign, at any width.
const SIGN_BOTTOM_FRACTION = {
  desktop: 0.4, // herov2.jpg (landscape)
  mobile: 0.53, // hero_mobile.jpg (portrait)
}

const Hero = () => {
  const cardRef = useRef(null)
  const imgRef = useRef(null)
  const [textTop, setTextTop] = useState(null)

  // Map the sign's position in the image to a pixel offset inside the card,
  // accounting for object-fit: cover scaling/cropping (object-position: center).
  const positionText = useCallback(() => {
    const card = cardRef.current
    const img = imgRef.current
    if (!card || !img) return

    const naturalW = img.naturalWidth
    const naturalH = img.naturalHeight
    if (!naturalW || !naturalH) return

    const W = card.clientWidth
    const H = card.clientHeight

    const scale = Math.max(W / naturalW, H / naturalH)
    const displayedH = naturalH * scale
    const offsetY = (H - displayedH) / 2 // object-position center

    const isDesktop = (img.currentSrc || img.src).includes('herov2')
    const fraction = isDesktop
      ? SIGN_BOTTOM_FRACTION.desktop
      : SIGN_BOTTOM_FRACTION.mobile

    const signBottom = offsetY + fraction * displayedH
    const gap = W >= 640 ? 28 : 16

    setTextTop(Math.round(signBottom + gap))
  }, [])

  useIsomorphicLayoutEffect(() => {
    positionText()

    const card = cardRef.current
    const ro = new ResizeObserver(() => positionText())
    if (card) ro.observe(card)
    window.addEventListener('resize', positionText)

    return () => {
      ro.disconnect()
      window.removeEventListener('resize', positionText)
    }
  }, [positionText])

  return (
    <section id="home" className="relative overflow-x-clip touch-pan-y" aria-label="Hero">
      {/* Image card with rounded corners on burnt orange background */}
      <div
        ref={cardRef}
        className="relative z-[2] h-[55vh] min-h-[340px] min-[500px]:h-[90vh] min-[500px]:min-h-[520px] sm:h-[92vh] sm:min-h-[560px] md:h-[95vh] md:min-h-[600px] rounded-b-[24px] md:rounded-b-[100px] overflow-hidden bg-cream-200 touch-pan-y"
      >
        <picture>
          {['avif', 'webp'].map((fmt) => (
            <source
              key={`d-${fmt}`}
              media="(min-width: 640px)"
              type={`image/${fmt}`}
              srcSet={srcSetFor('/herov2.jpg', fmt)}
              sizes="100vw"
            />
          ))}
          <source media="(min-width: 640px)" srcSet="/herov2.jpg" />
          {['avif', 'webp'].map((fmt) => (
            <source
              key={`m-${fmt}`}
              type={`image/${fmt}`}
              srcSet={srcSetFor('/hero_mobile.jpg', fmt)}
              sizes="100vw"
            />
          ))}
          <img
            ref={imgRef}
            src="/hero_mobile.jpg"
            alt="Recreo Rehab clinic sign at Yonge & Eglinton, Toronto"
            aria-hidden="true"
            draggable="false"
            fetchpriority="high"
            decoding="async"
            onLoad={positionText}
            className="w-full h-full object-cover object-center origin-center pointer-events-none select-none"
          />
        </picture>

        {/* Warm earthy overlay for text readability */}
        <div className="absolute inset-0 bg-gradient-to-b from-dark-900/45 via-dark-900/15 to-dark-900/50" />

        {/* Overlaid content — anchored just beneath the sign in the photo */}
        <div
          className="absolute left-0 right-0 flex flex-col items-center text-center px-4 sm:px-6 gap-4 sm:gap-8 md:gap-10"
          style={
            textTop === null
              ? { top: '50%', transform: 'translateY(-50%)' }
              : { top: textTop }
          }
        >
          <motion.div
            className="flex flex-col items-center gap-2 sm:gap-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.2, delay: 0.5, ease: 'easeOut' }}
          >
            <p className="font-script text-cream-100 text-3xl min-[500px]:text-4xl sm:text-6xl leading-[1.2] max-w-[min(92vw,42rem)]">
              feel <em className="italic">better</em> in your body
            </p>
            <h1 className="font-sans text-cream-100 text-[clamp(0.7rem,1.9vw,1rem)] font-semibold tracking-[0.22em] uppercase [text-shadow:0_1px_10px_rgba(0,0,0,0.55)]">
              Massage &amp; Athletic Therapy at Yonge &amp; Eglinton
            </h1>
          </motion.div>

          <motion.a
            href={BOOKING_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="group inline-flex items-center gap-3 sm:gap-4 rounded-full bg-cream-100 px-[clamp(1.5rem,4vw,2.5rem)] py-[clamp(0.65rem,2vw,1rem)] font-sans text-[clamp(0.65rem,1.8vw,0.875rem)] font-semibold tracking-[0.2em] sm:tracking-[0.25em] uppercase text-dark-800 shadow-lg transition-all duration-300 hover:bg-white hover:shadow-xl"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 1.2, ease: 'easeOut' }}
            whileHover={{ scale: 1.04 }}
            whileTap={{ scale: 0.97 }}
          >
            Book an Appointment
            <span className="inline-block transition-transform duration-300 group-hover:translate-x-1.5">→</span>
          </motion.a>
        </div>
      </div>
    </section>
  )
}

export default Hero
